import type { CSSProperties } from "react";

import type { EnabledCountdownView } from "../features/countdown/model";

export const ZERO_HOLD_MS = 5_000;
export const ZERO_FADE_MS = 5_000;

export interface ZeroBlackoutState {
  readonly stage: "holding" | "fading" | "black";
  readonly delayMs: number;
  readonly durationMs: number;
  readonly initialOpacity: number;
}

export function getZeroBlackoutState(elapsedMs: number): ZeroBlackoutState {
  const elapsed = Number.isFinite(elapsedMs) ? Math.max(0, elapsedMs) : 0;
  if (elapsed < ZERO_HOLD_MS) {
    return {
      stage: "holding",
      delayMs: ZERO_HOLD_MS - elapsed,
      durationMs: ZERO_FADE_MS,
      initialOpacity: 0,
    };
  }

  const fadeElapsed = elapsed - ZERO_HOLD_MS;
  if (fadeElapsed < ZERO_FADE_MS) {
    return {
      stage: "fading",
      delayMs: 0,
      durationMs: ZERO_FADE_MS - fadeElapsed,
      initialOpacity: fadeElapsed / ZERO_FADE_MS,
    };
  }

  return {
    stage: "black",
    delayMs: 0,
    durationMs: 0,
    initialOpacity: 1,
  };
}

interface CountdownSlideProps {
  readonly view: EnabledCountdownView;
  readonly fullscreen?: boolean;
  readonly zeroElapsedMs?: number;
  readonly onOpenSettings?: () => void;
}

interface CountdownToken {
  readonly value: string;
  readonly kind: "number" | "unit";
}

function tokenizeCountdown(value: string): readonly CountdownToken[] {
  const minuteMatch = /^(\d+)分(\d+)秒$/.exec(value);
  if (minuteMatch) {
    return [
      { value: minuteMatch[1] ?? "0", kind: "number" },
      { value: "分", kind: "unit" },
      { value: minuteMatch[2] ?? "00", kind: "number" },
      { value: "秒", kind: "unit" },
    ];
  }

  const secondsMatch = /^(\d+)秒$/.exec(value);
  return [
    { value: secondsMatch?.[1] ?? value, kind: "number" },
    { value: secondsMatch ? "秒" : "", kind: "unit" },
  ];
}

export function CountdownSlide({
  view,
  fullscreen = false,
  zeroElapsedMs = 0,
  onOpenSettings,
}: CountdownSlideProps) {
  const tokens = tokenizeCountdown(view.formattedRemaining);
  const heading = view.isStopped ? "開演時刻です" : "前夜祭 開始まで";
  const normalizedZeroElapsedMs = Number.isFinite(zeroElapsedMs)
    ? Math.max(0, zeroElapsedMs)
    : 0;
  const blackout = view.isStopped
    ? getZeroBlackoutState(normalizedZeroElapsedMs)
    : null;
  const blackoutStyle = blackout === null
    ? undefined
    : {
        // Opacity comes directly from synchronized elapsed time. The short CSS
        // transition only smooths the clock's 200ms render cadence.
        "--blackout-opacity": String(blackout.initialOpacity),
      } as CSSProperties;

  const content = (
    <article
      className={`countdown-slide countdown-slide--${view.phase} ${fullscreen ? "countdown-slide--fullscreen countdown-slide--fullscreen-entry" : ""}`}
      data-phase={view.phase}
      data-testid="countdown-display"
    >
      <div className="countdown-orbit countdown-orbit--outer" aria-hidden="true" />
      <div className="countdown-orbit countdown-orbit--inner" aria-hidden="true" />
      <div className="countdown-content">
        <p>{heading}</p>
        <div className="countdown-value" aria-label={view.formattedRemaining}>
          {tokens.map((token, index) =>
            token.kind === "number" ? (
              <strong className="countdown-number" key={`${token.value}-${index}`}>{token.value}</strong>
            ) : (
              <span className="countdown-unit" key={`${token.value}-${index}`}>{token.value}</span>
            ),
          )}
        </div>
        <small>
          {view.isStopped
            ? "NIJIIRO FESTIVAL 2026"
            : "COUNTDOWN TO OPENING"}
        </small>
      </div>
      {blackout ? (
        <div
          className={`countdown-zero-blackout ${blackout.stage === "black" ? "countdown-zero-blackout--black" : "countdown-zero-blackout--tracking"}`}
          style={blackoutStyle}
          aria-hidden="true"
          data-testid="countdown-blackout"
          data-blackout-stage={blackout.stage}
          data-zero-elapsed-ms={Math.floor(normalizedZeroElapsedMs)}
        />
      ) : null}
    </article>
  );

  if (!fullscreen) return content;

  return (
    <div
      className="countdown-fullscreen"
      role="button"
      tabIndex={0}
      aria-label={`${view.formattedRemaining}。設定を開く`}
      onClick={onOpenSettings}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") onOpenSettings?.();
      }}
    >
      {content}
    </div>
  );
}
